import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { take } from 'rxjs/operators';

import { IMultiSelectOption, IMultiSelectSettings, IMultiSelectTexts } from 'ngx-bootstrap-multiselect';
import { ApiError, MessageService } from '../../../../../core';
import { TipoArquivoSearchOrderBy } from '../../tipo-arquivo-search-orderby';
import { TipoArquivoFilter } from '../../filter';
import { TipoArquivoSearchHttpService } from '../../services';
import { TipoArquivoSituacao } from '../../situacao';
import { TipoArquivoSearch } from '../../tipo-arquivo-search';

@Component({
    selector: 'sgt-tipo-arquivo-multiselect',
    templateUrl: './tipo-arquivo-multiselect.component.html',
    styleUrls: ['./tipo-arquivo-multiselect.component.less']
})
export class TipoArquivoMultiselectComponent implements OnInit, OnDestroy {
    /** Tipos de arquivos selecionados */
    @Input() idsTiposArquivos: number[];
    /** Ao alterar a seleção dos tipos de arquivos */
    @Output() idsTiposArquivosChange = new EventEmitter<number[]>();

    /** Se deve exibir a opção de selecionar a opção de tipo de arquivo nulo ou seja, para filtrar registros que não possuem o tipo de arquivo */
    @Input() showNullOption = false;
    /** Valor da opção de selecionar o tipo de arquivo nulo */
    @Input() nullOption = false;
    /** Ao alterar a seleção da opção do tipo de arquivo nulo */
    @Output() nullOptionChange = new EventEmitter<boolean>();

    /** Se deve pesquisar os tipos de arquivos ao iniciar o component */
    @Input() searchOnInit = true;

    /** Filtro da referência dos tipos de arquivos */
    @Input() referenciaFilter: number;
    /** Se deve filtrar somente os tipos de arquivos ativos */
    @Input() somenteAtivos = false;

    /** Ao destruir o multiselect */
    @Output() onDestroy = new EventEmitter<void>();

    // Settings
    settings: IMultiSelectSettings;
    @Input() pullRight = false;
    @Input() enableSearch = true;
    @Input() showCheckAll = true;
    @Input() showUncheckAll = true;
    @Input() dynamicTitleMaxItems = 0;
    @Input() fixedTitle = false;
    /** Se deve exibir o multiselect acima */
    @Input() dropup = false;
    /** Se deve desabilitar o multiselect */
    @Input() disabled = false;

    // Texts
    texts: IMultiSelectTexts;
    @Input() checkAll = 'Selecionar Todos';
    @Input() uncheckAll = 'Limpar';
    @Input() defaultTitle = 'Nenhum Tipo';
    @Input() checked = 'Tipo';
    @Input() checkedPlural = 'Tipos';
    @Input() searchPlaceholder = 'Pesquisar...';
    @Input() allSelected = 'Todo Selecionados';
    @Input() searchEmptyResult = 'Nenhum Tipo Encontrado';
    @Input() searchNoRenderText = 'Digite para pesquisar';

    // Options
    options: IMultiSelectOption[];

    tiposArquivos: TipoArquivoSearch[];

    constructor(private messageService: MessageService,
                private tipoArquivoSearchHttpService: TipoArquivoSearchHttpService) { }

    ngOnInit() {
        // Settings
        this.settings = {
            pullRight: this.pullRight,
            enableSearch: this.enableSearch,
            containerClasses: `container-multiselect container-multiselect-tipo-arquivo ${this.dropup ? 'dropup' : 'dropdown'}`,
            showCheckAll: this.showCheckAll,
            showUncheckAll: this.showUncheckAll,
            dynamicTitleMaxItems: this.dynamicTitleMaxItems,
            fixedTitle: this.fixedTitle
        };
        // Texts
        this.texts = {
            checkAll: this.checkAll,
            uncheckAll: this.uncheckAll,
            defaultTitle: this.defaultTitle,
            checked: this.checked,
            checkedPlural: this.checkedPlural,
            searchPlaceholder: this.searchPlaceholder,
            allSelected: this.allSelected,
            searchEmptyResult: this.searchEmptyResult,
            searchNoRenderText: this.searchNoRenderText
        };

        if (this.searchOnInit) {
            this.search();
        }
    }


    ngOnChanges(changes: SimpleChanges) {
        for (const propName in changes) {
            if (propName === 'referenciaFilter') {
                if (!changes[propName].isFirstChange()) {
                    this.search();
                }
            }
        }
    }

    /** Cria o filter para pesquisar os tipos de arquivos */
    private createFilter(): TipoArquivoFilter {
        const filter = new TipoArquivoFilter();

        // Referência
        if (this.referenciaFilter > 0) {
            filter.referencias = [this.referenciaFilter];
        }
        // Somente ativas
        filter.situacoes = this.somenteAtivos ? [TipoArquivoSituacao.ATIVO] : null;

        return filter;
    }

    /** Pesquisa e cria os tipos de arquivos para o multiselect */
    private search() {
        this.tiposArquivos = [];
        this.options = [];
        const selects: IMultiSelectOption[] = [];

        const filter  = this.createFilter();
        this.tipoArquivoSearchHttpService.search(filter, TipoArquivoSearchOrderBy.Nome, 'asc', -1, -1)
            .pipe(
                take(1)
            )
            .subscribe(
                tiposArquivos => {
                    // Adicionando os tipos de arquivos ao multiselect
                    if (tiposArquivos) {
                        // Adicionando a opção de "Sem Tipo"
                        if (this.showNullOption) {
                            selects.push({
                                id: 0,
                                name: 'Sem Tipo'
                            });
                        }

                        // Tipos de arquivos
                        tiposArquivos.forEach(tipoArquivo => {
                            selects.push({
                                id: tipoArquivo.id,
                                name: tipoArquivo.nome
                            });
                        });
                        this.options = selects;
                    }
                },
                (error: ApiError) => {
                    this.messageService.error(error.message,  'Erro ao pesquisar os tipos de arquivos', error);
                }
            );
    }

    /** Ao alterar a seleção da opção de tipo de arquivo nulo */
    private onChangeNullOption() {
        this.nullOption = this.idsTiposArquivos.includes(0);
        this.nullOptionChange.emit(this.nullOption);

    }

    /** Ao alterar a seleção dos tipos de arquivos */
    onChange() {
        this.onChangeNullOption();
        this.idsTiposArquivosChange.emit(this.idsTiposArquivos);
    }

    ngOnDestroy() {
        this.onDestroy.emit();
    }
}
